import { useRef, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import {
  ArrowLeft,
  BadgePercent,
  CalendarDays,
  Copy,
  CreditCard,
  ExternalLink,
  FileText,
  Mail,
  MapPin,
  Phone,
  Rocket,
  ShieldCheck,
  Store,
  Upload,
  User,
} from "lucide-react";
import {
  PROOF_MAX_BYTES,
  PROOF_MIME_TYPES,
  formatPrice,
  proofMimeSchema,
  type Order,
  type OrderStatus,
  type Purchase,
} from "@nh/shared";
import { GlassPanel, PageHeader } from "@/shared/ui/layout";
import { Alert, Badge, Button, Select } from "@/shared/ui/core";
import { SkeletonTable } from "@/shared/ui/ui";
import {
  AdminGuard,
  DataTable,
  DataTableFrame,
  Td,
  Th,
  Tr,
} from "@/features/admin/components";
import { useApiErrorMessage } from "@/features/admin/products/useApiErrorMessage";
import {
  useAdminOrder,
  useCreateOrderStripeLink,
  useUpdateOrderStatus,
  useUploadOrderProof,
} from "./orders.queries";
import { OrderStatusBadge, useOrderStatusOptions } from "./OrderStatusBadge";
import { ShippingCard } from "./ShippingCard";
import type { OrderDetail } from "./orders.api";
import { formatOrderDate } from "./format";

/**
 * ═══════════════════════════════════════════════════════════════════
 * El detalle de una orden, rediseñado (Anahí, 27-ago).
 *
 * ── El layout: dos columnas con barra lateral pegajosa ──────────────
 * Es el patrón "sticky sidebar with content" de sus referencias. La
 * IZQUIERDA trabaja: primero la VALIDACIÓN DEL PAGO —comprobante y
 * cambio de estado, que es lo que activa la franquicia, el paquete y
 * las comisiones—, luego los renglones y los puntos/comisiones. La
 * DERECHA acompaña: quién compró, a dónde va y el resumen del dinero —
 * visible mientras se recorre lo demás, sin volver a subir.
 *
 * ── La jerarquía la dictó ella ───────────────────────────────────────
 * "Lo principal que debe destacar es ver el comprobante o subir el
 * comprobante y cambiar el estado de la orden." Por eso la validación
 * abre la página con su propio marco acentuado, y no vive al fondo.
 * ═══════════════════════════════════════════════════════════════════
 */
export function AdminOrderDetailPage() {
  return (
    <AdminGuard>
      <OrderDetailScreen />
    </AdminGuard>
  );
}

function OrderDetailScreen() {
  const { t } = useTranslation();
  const { id } = useParams<{ id: string }>();
  const detail = useAdminOrder(id);

  if (detail.isPending) {
    return (
      <div className="p-5" aria-busy="true" aria-live="polite">
        <span className="sr-only">{t("Cargando la orden…")}</span>
        <SkeletonTable rows={5} columns={4} />
      </div>
    );
  }

  if (detail.isError || !detail.data) {
    return (
      <Alert variant="error" title={t("No se pudo cargar la orden")}>
        <Link to="/admin/orders" className="underline underline-offset-2">
          {t("Volver a las órdenes")}
        </Link>
      </Alert>
    );
  }

  return <OrderDetailBody detail={detail.data} />;
}

function OrderDetailBody({ detail }: { detail: OrderDetail }) {
  const { order, purchases, commissions } = detail;
  const { t } = useTranslation();

  return (
    <>
      <PageHeader
        title={order.folio}
        description={`${order.customer.name} · ${formatOrderDate(order.createdAt)}`}
        actions={
          <Button
            as={Link}
            to="/admin/orders"
            variant="secondary"
            size="sm"
            leftIcon={<ArrowLeft />}
          >
            {t("Todas las órdenes")}
          </Button>
        }
      />

      <div className="grid items-start gap-4 xl:grid-cols-[1fr_360px]">
        {/* ── Columna de TRABAJO ─────────────────────────────────────── */}
        <div className="grid min-w-0 gap-4">
          <ValidationHero order={order} />
          {/* El envío con Trasso: aparece con el pago validado. */}
          <ShippingCard order={order} />
          <OrderItemsCard order={order} />
          <PointsCommissionsCard purchases={purchases} commissions={commissions} />
        </div>

        {/* ── Barra lateral: el CONTEXTO, siempre a la vista ─────────── */}
        <div className="grid gap-4 xl:sticky xl:top-24">
          <CustomerCard order={order} />
          <DeliveryCard order={order} />
          <SummaryCard order={order} />
        </div>
      </div>
    </>
  );
}

// ═════════════════════════════════════════════════════════════════════
// La validación del pago — la protagonista
// ═════════════════════════════════════════════════════════════════════

function ValidationHero({ order }: { order: Order }) {
  const { t } = useTranslation();

  return (
    <GlassPanel className="relative animate-apple-fade-in p-5 ring-1 ring-ds-violet-pop/25 sm:p-6">
      <div className="relative grid gap-5">
        <div className="flex flex-wrap items-center gap-3">
          <span className="flex h-11 w-11 items-center justify-center rounded-2xl bg-ds-violet-pop text-white">
            <ShieldCheck className="h-5 w-5" />
          </span>
          <div className="min-w-0">
            <h2 className="text-[17px] font-semibold text-ds-ink">
              {t("Validación del pago")}
            </h2>
            <p className="text-[13px] text-ds-ink-3">
              {order.isFranchise
                ? t("Aprobar activa la franquicia y sus comisiones.")
                : t("Aprobar convierte la orden en compras.")}
            </p>
          </div>
          <div className="ml-auto">
            <OrderStatusBadge status={order.status} />
          </div>
        </div>

        <div className="grid gap-5 lg:grid-cols-2">
          <OrderProofBlock order={order} />
          <OrderStatusBlock order={order} />
        </div>
      </div>
    </GlassPanel>
  );
}

// ─── El comprobante ──────────────────────────────────────────────────

/** De `File` a lo que viaja: base64 sin el prefijo `data:`. */
async function fileToBase64(file: File): Promise<string> {
  const dataUrl = await new Promise<string>((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(String(reader.result));
    reader.onerror = () => reject(new Error("No se pudo leer el archivo"));
    reader.readAsDataURL(file);
  });
  return dataUrl.slice(dataUrl.indexOf(",") + 1);
}

function OrderProofBlock({ order }: { order: Order }) {
  const { t } = useTranslation();
  const inputRef = useRef<HTMLInputElement>(null);
  const [localError, setLocalError] = useState<string | null>(null);
  const upload = useUploadOrderProof(order.id);
  const uploadError = useApiErrorMessage(upload.error);

  const proof = order.paymentProof;

  async function onFile(file: File | undefined) {
    setLocalError(null);
    if (!file) return;

    const mime = proofMimeSchema.safeParse(file.type);
    if (!mime.success) {
      setLocalError(t("Solo se aceptan fotos (JPG, PNG, WebP) o PDF."));
      return;
    }
    if (file.size > PROOF_MAX_BYTES) {
      setLocalError(t("El comprobante no puede pasar de 4 MB."));
      return;
    }

    const dataBase64 = await fileToBase64(file);
    await upload.mutateAsync({ fileName: file.name, mimeType: mime.data, dataBase64 });
  }

  return (
    <div className="grid content-start gap-3 rounded-2xl border border-ds-border bg-ds-cream-2/40 p-4">
      <h3 className="flex items-center gap-2 text-[14px] font-semibold text-ds-ink">
        <FileText className="h-4 w-4 text-ds-ink-3" />
        {t("Comprobante de pago")}
      </h3>

      {proof ? (
        <>
          <p className="text-[12px] text-ds-ink-3">
            {proof.fileName} · {(proof.sizeBytes / 1024).toFixed(0)} KB ·{" "}
            {t("subido el {{fecha}}", { fecha: formatOrderDate(proof.uploadedAt) })}
          </p>
          {proof.dataUrl &&
            (proof.mimeType === "application/pdf" ? (
              <a
                href={proof.dataUrl}
                download={proof.fileName}
                className="inline-flex items-center gap-2 text-[13px] text-ds-violet-pop underline-offset-2 hover:underline"
              >
                <FileText className="h-4 w-4" />
                {t("Descargar el PDF")}
              </a>
            ) : (
              <img
                src={proof.dataUrl}
                alt={t("Comprobante de pago de {{folio}}", { folio: order.folio })}
                className="max-h-72 w-fit rounded-xl border border-ds-border transition hover:shadow-ds-float"
              />
            ))}
        </>
      ) : (
        <p className="text-[13px] leading-relaxed text-ds-ink-3">
          {t("Todavía no hay comprobante. Sube la foto o el PDF del cliente.")}
        </p>
      )}

      <div>
        <input
          ref={inputRef}
          type="file"
          accept={PROOF_MIME_TYPES.join(",")}
          className="sr-only"
          aria-label={t("Elegir comprobante")}
          onChange={(event) => {
            void onFile(event.target.files?.[0]);
            event.target.value = "";
          }}
        />
        <Button
          variant="secondary"
          size="sm"
          leftIcon={<Upload />}
          loading={upload.isPending}
          onClick={() => inputRef.current?.click()}
        >
          {proof ? t("Reemplazar comprobante") : t("Subir comprobante")}
        </Button>
      </div>

      {localError && (
        <Alert variant="error" onDismiss={() => setLocalError(null)}>
          {localError}
        </Alert>
      )}
      {upload.isError && (
        <Alert variant="error" onDismiss={() => upload.reset()}>
          {uploadError}
        </Alert>
      )}
    </div>
  );
}

// ─── El estado ───────────────────────────────────────────────────────

function OrderStatusBlock({ order }: { order: Order }) {
  const { t } = useTranslation();
  const options = useOrderStatusOptions();
  const [selected, setSelected] = useState<OrderStatus>(order.status);
  const [createdPurchases, setCreatedPurchases] = useState<Purchase[]>([]);
  const update = useUpdateOrderStatus(order.id);
  const updateError = useApiErrorMessage(update.error);

  async function cambiar() {
    setCreatedPurchases([]);
    const result = await update.mutateAsync(selected);
    setCreatedPurchases(result.createdPurchases);
  }

  return (
    <div className="grid content-start gap-3 rounded-2xl border border-ds-border bg-ds-cream-2/40 p-4">
      <h3 className="flex items-center gap-2 text-[14px] font-semibold text-ds-ink">
        <ShieldCheck className="h-4 w-4 text-ds-ink-3" />
        {t("Estado de la orden")}
      </h3>

      {order.purchasedAt && (
        <p className="text-[12px] leading-relaxed text-ds-ink-3">
          {t("Convertida en compras el {{fecha}}:", {
            fecha: formatOrderDate(order.purchasedAt),
          })}{" "}
          <span className="font-mono">{(order.purchaseFolios ?? []).join(" · ")}</span>
          {" — "}
          <Link to="/admin/purchases" className="underline underline-offset-2">
            {t("ver compras")}
          </Link>
        </p>
      )}

      <div className="flex flex-wrap items-end gap-3">
        {/*
          El `Select` del sistema, no un `<select>` desnudo: la flecha
          del nativo se pegaba al borde derecho de una caja demasiado
          ancha (Anahí, 27-ago). El del sistema trae su chevron bien
          colocado, y el ancho lo fija el contenedor.
        */}
        <div className="w-44">
          <Select
            label={t("Nuevo estado")}
            options={options}
            value={selected}
            onChange={(event) => setSelected(event.target.value as OrderStatus)}
          />
        </div>
        <Button
          size="sm"
          loading={update.isPending}
          disabled={selected === order.status}
          onClick={() => void cambiar()}
        >
          {t("Cambiar estado")}
        </Button>
      </div>

      {!order.purchasedAt && (
        <p className="text-[12px] leading-relaxed text-ds-ink-3">
          {t("Al pasar a Pagada, la orden se vuelve compras con sus puntos.")}
        </p>
      )}

      {update.isError && (
        <Alert variant="error" onDismiss={() => update.reset()}>
          {updateError}
        </Alert>
      )}

      {createdPurchases.length > 0 && (
        <Alert variant="success" title={t("La orden se convirtió en compras")}>
          <span className="font-mono">
            {createdPurchases.map((purchase) => purchase.folio).join(" · ")}
          </span>{" "}
          <Link to="/admin/purchases" className="underline underline-offset-2">
            {t("Verlas en Compras")}
          </Link>
        </Alert>
      )}
    </div>
  );
}

// ═════════════════════════════════════════════════════════════════════
// Los renglones
// ═════════════════════════════════════════════════════════════════════

function OrderItemsCard({ order }: { order: Order }) {
  const { t } = useTranslation();

  return (
    <DataTableFrame className="animate-apple-fade-in">
      <DataTable caption={t("Renglones de la orden")}>
        <thead>
          <tr>
            <Th>{t("Producto")}</Th>
            <Th align="right">{t("Cantidad")}</Th>
            <Th align="right">{t("Precio unitario")}</Th>
            <Th align="right">{t("Importe")}</Th>
          </tr>
        </thead>
        <tbody>
          {order.items.map((item, index) => (
            <Tr key={`${item.productId}-${index}`}>
              <Td>{item.name}</Td>
              <Td align="right">{item.quantity}</Td>
              <Td align="right">{formatPrice(item.unitPriceCents, order.currency)}</Td>
              <Td align="right" className="font-medium">
                {formatPrice(item.quantity * item.unitPriceCents, order.currency)}
              </Td>
            </Tr>
          ))}
          <Tr>
            <Td colSpan={3} className="text-right font-semibold">
              {t("Total")}
            </Td>
            <Td align="right" className="font-semibold">
              {formatPrice(order.totalCents, order.currency)}
            </Td>
          </Tr>
        </tbody>
      </DataTable>
    </DataTableFrame>
  );
}

// ═════════════════════════════════════════════════════════════════════
// Puntos y comisiones
// ═════════════════════════════════════════════════════════════════════

/**
 * SOLO lo que de verdad ocurrió (Anahí, 27-ago): las compras generadas
 * con sus puntos, y las comisiones REGISTRADAS por el motor al validar.
 * Sin validar, la tarjeta no existe; sin comisiones, esa sección no se
 * pinta — enseñar un reparto que no ocurrió confunde.
 */
function PointsCommissionsCard({
  purchases,
  commissions,
}: {
  purchases: Purchase[];
  commissions: OrderDetail["commissions"];
}) {
  const { t } = useTranslation();

  if (purchases.length === 0) return null;

  const totalPV = purchases.reduce((sum, p) => sum + p.volumePoints, 0);
  const totalCV = purchases.reduce((sum, p) => sum + p.commissionPoints, 0);
  const totalCommissions = commissions.reduce((sum, c) => sum + c.amountCents, 0);

  return (
    <GlassPanel className="relative animate-apple-fade-in p-5">
      <div className="relative grid gap-4">
        <h2 className="flex items-center gap-2 text-[15px] font-semibold text-ds-ink">
          <BadgePercent className="h-4 w-4 text-ds-ink-3" />
          {t("Puntos y comisiones")}
        </h2>

        <div className="grid gap-2">
          <div className="grid grid-cols-3 gap-3">
            <StatTile label={t("PV generados")} value={String(totalPV)} />
            <StatTile label={t("CV generados")} value={String(totalCV)} />
            <StatTile label={t("Compras")} value={String(purchases.length)} />
          </div>
          <ul className="grid gap-1.5">
            {purchases.map((purchase) => (
              <li
                key={purchase.folio}
                className="flex flex-wrap items-center justify-between gap-2 rounded-xl border border-ds-border bg-white px-3 py-2 text-[13px]"
              >
                <span className="font-mono">{purchase.folio}</span>
                <span className="text-ds-ink-3">{purchase.productName}</span>
                <span className="ml-auto flex gap-3 text-ds-ink">
                  <span>{purchase.volumePoints} PV</span>
                  <span>{purchase.commissionPoints} CV</span>
                  <span className="font-medium">
                    {formatPrice(purchase.amountCents, purchase.currency)}
                  </span>
                </span>
              </li>
            ))}
          </ul>
        </div>

        {commissions.length > 0 && (
          <div className="grid gap-2 rounded-2xl border border-ds-border bg-ds-cream-2/40 p-4">
            <p className="flex items-center gap-2 text-[13px] font-semibold text-ds-ink">
              <Rocket className="h-4 w-4 text-ds-violet-pop" />
              {t("Comisiones generadas")}
            </p>
            <ul className="grid gap-1.5">
              {commissions.map((commission) => (
                <li
                  key={commission.id}
                  className="flex flex-wrap items-center justify-between gap-2 rounded-xl border border-ds-border bg-white px-3 py-2 text-[13px]"
                >
                  <span className="text-ds-ink-3">
                    {t("Nivel {{n}}", { n: commission.level ?? 0 })} · {commission.bonusType} ·{" "}
                    {commission.bpsApplied / 100}%
                  </span>
                  <span className="font-medium text-ds-ink">{commission.userName}</span>
                  <span className="ml-auto font-semibold text-ds-ink">
                    {formatPrice(commission.amountCents, commission.currency)}
                  </span>
                </li>
              ))}
            </ul>
            <p className="text-[12px] text-ds-ink-3">
              {t("Total: {{total}}, pendiente de liberar.", {
                total: formatPrice(totalCommissions, commissions[0]?.currency ?? "MXN"),
              })}
            </p>
          </div>
        )}
      </div>
    </GlassPanel>
  );
}

function StatTile({ label, value }: { label: string; value: string }) {
  return (
    <div className="rounded-2xl border border-ds-border bg-white px-3 py-2.5 text-center transition hover:shadow-ds-float">
      <p className="text-[20px] font-semibold text-ds-ink">{value}</p>
      <p className="text-[11px] uppercase tracking-ds-label text-ds-ink-3">{label}</p>
    </div>
  );
}

// ═════════════════════════════════════════════════════════════════════
// La barra lateral: cliente, entrega y resumen
// ═════════════════════════════════════════════════════════════════════

function InfoRow({
  icon: Icon,
  label,
  children,
}: {
  icon: typeof Mail;
  label: string;
  children: React.ReactNode;
}) {
  return (
    <div className="flex items-start gap-3">
      <span className="mt-0.5 flex h-8 w-8 shrink-0 items-center justify-center rounded-xl bg-ds-mist text-ds-ink-3">
        <Icon className="h-4 w-4" />
      </span>
      <div className="min-w-0">
        <p className="text-[11px] font-semibold uppercase tracking-ds-label text-ds-ink-3">
          {label}
        </p>
        <div className="text-[13px] leading-relaxed text-ds-ink">{children}</div>
      </div>
    </div>
  );
}

/** Las iniciales del cliente, para el avatar de la tarjeta. */
function initialsOf(name: string): string {
  return name
    .trim()
    .split(/\s+/)
    .slice(0, 2)
    .map((word) => word.charAt(0))
    .join("")
    .toUpperCase();
}

function CustomerCard({ order }: { order: Order }) {
  const { t } = useTranslation();
  const { customer } = order;

  return (
    <GlassPanel className="relative animate-apple-fade-in p-5">
      <div className="relative grid gap-4">
        <div className="flex items-center gap-3">
          <span className="flex h-11 w-11 items-center justify-center rounded-full bg-ds-violet-pop text-[15px] font-semibold text-white">
            {initialsOf(customer.name) || <User className="h-5 w-5" />}
          </span>
          <div className="min-w-0">
            <h2 className="truncate text-[15px] font-semibold text-ds-ink">
              {customer.name}
            </h2>
            <p className="text-[12px] text-ds-ink-3">{t("Cliente")}</p>
          </div>
        </div>

        <div className="grid gap-3">
          <InfoRow icon={Mail} label={t("Correo")}>
            <a
              href={`mailto:${customer.email}`}
              className="break-all underline-offset-2 hover:underline"
            >
              {customer.email}
            </a>
          </InfoRow>
          {customer.phone && (
            <InfoRow icon={Phone} label={t("Teléfono")}>
              <a href={`tel:${customer.phone}`} className="underline-offset-2 hover:underline">
                {customer.phone}
              </a>
            </InfoRow>
          )}
        </div>
      </div>
    </GlassPanel>
  );
}

const PAYMENT_METHOD_LABELS: Record<string, string> = {
  card: "Tarjeta (Stripe)",
  transfer: "Transferencia",
  cash: "Efectivo",
};

function DeliveryCard({ order }: { order: Order }) {
  const { t } = useTranslation();
  const { customer, paymentMethod, storeSlug } = order;

  return (
    <GlassPanel className="relative animate-apple-fade-in p-5">
      <div className="relative grid gap-3">
        <h2 className="text-[15px] font-semibold text-ds-ink">{t("Entrega y pago")}</h2>

        {customer.address ? (
          <InfoRow icon={MapPin} label={t("Dirección de envío")}>
            <p className="whitespace-pre-line">{customer.address}</p>
          </InfoRow>
        ) : (
          <InfoRow icon={MapPin} label={t("Dirección de envío")}>
            <p className="text-ds-ink-3">{t("Sin dirección: pedido sin envío o del panel.")}</p>
          </InfoRow>
        )}

        {paymentMethod && (
          <InfoRow icon={CreditCard} label={t("Método de pago")}>
            {t(PAYMENT_METHOD_LABELS[paymentMethod] ?? paymentMethod)}
            {paymentMethod === "card" && <StripeLink order={order} />}
          </InfoRow>
        )}

        {storeSlug && (
          <InfoRow icon={Store} label={t("Vendido en")}>
            <Link to={`/shop/t/${storeSlug}`} className="underline-offset-2 hover:underline">
              /shop/t/{storeSlug}
            </Link>
          </InfoRow>
        )}
      </div>
    </GlassPanel>
  );
}

/**
 * El link de pago de Stripe (Anahí, 22-oct-2026): para abrirlo y cobrar
 * si el cliente lo perdió o si el vendedor hace el pago. Stripe lo vence
 * a las 24 h. Mientras siga vigente es EL link (22-oct: sin botón para
 * otro, para no cobrar dos veces); solo vencido o inexistente se genera
 * uno nuevo, y el servidor antes confirma con Stripe que no se pagó.
 */
function StripeLink({ order }: { order: Order }) {
  const { t } = useTranslation();
  const generar = useCreateOrderStripeLink(order.id);
  const error = useApiErrorMessage(generar.error);
  const [copiado, setCopiado] = useState(false);
  // La hora de cuando se abrió el detalle: suficiente para saber si el link venció.
  const [ahora] = useState(() => Date.now());

  if (order.status !== "pending") return null;

  const link = order.stripeCheckout;
  const vigente = link && (!link.expiresAt || new Date(link.expiresAt).getTime() > ahora);

  async function copiar(url: string) {
    try {
      await navigator.clipboard.writeText(url);
      setCopiado(true);
      window.setTimeout(() => setCopiado(false), 2000);
    } catch {
      // Sin permiso de portapapeles: el enlace sigue a la vista para abrirlo.
    }
  }

  return (
    <div className="mt-2 grid gap-2">
      {link && vigente ? (
        <>
          <a
            href={link.url}
            target="_blank"
            rel="noopener noreferrer"
            className="break-all text-[13px] text-ds-violet-pop underline-offset-2 hover:underline"
          >
            {t("Abrir link de pago de Stripe")}
          </a>
          {link.expiresAt && (
            <p className="text-[12px] text-ds-ink-3">
              {t("Vence el {{fecha}}", { fecha: formatOrderDate(link.expiresAt) })}
            </p>
          )}
          <div className="flex flex-wrap gap-2">
            <Button size="sm" variant="secondary" leftIcon={<Copy />} onClick={() => void copiar(link.url)}>
              {copiado ? t("Copiado") : t("Copiar link")}
            </Button>
          </div>
        </>
      ) : (
        <>
          <p className="text-[12px] text-ds-ink-3">
            {link ? t("El link de pago anterior ya venció.") : t("Esta orden no tiene un link de pago guardado.")}
          </p>
          <div>
            <Button size="sm" variant="secondary" leftIcon={<ExternalLink />} loading={generar.isPending} onClick={() => generar.mutate()}>
              {t("Generar link de pago")}
            </Button>
          </div>
        </>
      )}
      {error && <Alert variant="error">{error}</Alert>}
    </div>
  );
}

function SummaryCard({ order }: { order: Order }) {
  const { t } = useTranslation();

  return (
    <GlassPanel className="relative animate-apple-fade-in p-5">
      <div className="relative grid gap-3">
        <h2 className="text-[15px] font-semibold text-ds-ink">{t("Resumen")}</h2>

        <p className="text-[30px] font-semibold leading-none text-ds-ink">
          {formatPrice(order.totalCents, order.currency)}
          <span className="ml-1 text-[13px] font-normal text-ds-ink-3">{order.currency}</span>
        </p>

        <div className="flex flex-wrap gap-2">
          {order.isFranchise && (
            <Badge variant="indigo" size="sm" dot>
              {t("Pedido de franquicia")}
            </Badge>
          )}
          <OrderStatusBadge status={order.status} />
        </div>

        <InfoRow icon={CalendarDays} label={t("Creada")}>
          {formatOrderDate(order.createdAt)}
        </InfoRow>
      </div>
    </GlassPanel>
  );
}
